import dayjs from "dayjs";
import { Link } from "next-view-transitions";
import Image from "next/image";

import { Card } from "@/components/card";
import { HTMLMarkdown } from "@/components/markdown/HTMLMarkdown";
import { links } from "@/data/links";
import { Lang } from "@/i18n-config";
import { getTranslations } from "@/utils/i18n/get-translations";

import { ArticleInfo } from "../../services/article-info";

export const ArticleCard = ({
  lang,
  slug,
  title,
  content,
  coverPicture,
  previewPicture,
  publishedAt,
  primaryCategory,
  secondaryCategory,
  index,
}: ArticleInfo & { lang: Lang; index: number }) => {
  const encodedSlug = encodeURIComponent(slug ?? "");
  const { t } = getTranslations(lang);
  return (
    <Card
      className="flex flex-col gap-5"
      data-testid={`article-card-${index}`}
      data-slug={slug}
    >
      <div className="flex gap-3.5 @4xl:gap-7">
        <Link
          href={`/${lang}${links.myBlogDetail({ slug: encodedSlug })}`}
          className="aspect-1 h-full w-full max-w-[60px] shrink overflow-hidden @lg:max-w-[200px] @4xl:max-w-[230px]"
        >
          <Image
            priority
            src={previewPicture ?? coverPicture ?? ""}
            width={230}
            height={230}
            alt="article-image"
            placeholder="blur"
            blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mPk8cr7DwAC8wHF8e4zAAAAAABJRU5ErkJggg=="
            className="aspect-1 h-full w-full object-cover object-center transition-all hover:scale-105 hover:opacity-90"
          />
        </Link>
        <div className="flex w-full flex-col gap-3.5">
          <p className="font-bold text-green-700">
            <span className="underline underline-offset-2">
              {t.blog.category[primaryCategory]}
            </span>
            /
            <span className="underline underline-offset-2">
              {t.blog.category[secondaryCategory]}
            </span>
          </p>
          <Link href={`/${lang}${links.myBlogDetail({ slug: encodedSlug })}`}>
            <h2
              className="text-sm font-bold transition-colors hover:text-green-700 @4xl:text-xl"
              data-testid="article-title"
            >
              {title}
            </h2>
          </Link>
          <div className="mt-1 hidden text-zinc-800 @lg:block">
            <span className="line-clamp-6 overflow-ellipsis">
              <HTMLMarkdown>
                {content?.blocks
                  ?.find((block) => block.type === "paragraph")
                  ?.data.text.replace(/<\/?[^>]+(>|$)/g, "")}
              </HTMLMarkdown>
            </span>
          </div>
          <div className="mt-auto hidden justify-between @4xl:flex">
            <p>
              {t.blog.updatedAt}
              {dayjs(publishedAt).format("YYYY-MM-DD").toString()}
            </p>
            <Link href={`/${lang}${links.myBlogDetail({ slug: encodedSlug })}`}>
              <p className="text-sm font-bold text-green-700 underline underline-offset-2 transition-colors hover:text-green-600">
                {t.blog.readMore}
              </p>
            </Link>
          </div>
        </div>
      </div>

      <p
        className="line-clamp-6 overflow-ellipsis @lg:hidden"
        dangerouslySetInnerHTML={{
          __html: content?.blocks
            ?.find((block) => block.type === "paragraph")
            ?.data.text.replace(/<[^>]*>/g, ""),
        }}
      />
      <div className="flex justify-between text-sm text-zinc-800 @4xl:hidden">
        <p>
          {t.blog.updatedAt}
          {publishedAt}
        </p>
        <Link
          href={`/${lang}${links.myBlogDetail({
            slug: encodedSlug,
          })}`}
        >
          <p className="text-sm font-bold text-green-700 underline underline-offset-2 transition-colors hover:text-green-600">
            {t.blog.readMore}
          </p>
        </Link>
      </div>
    </Card>
  );
};
